import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Hash,
  Mail,
  MessageSquare,
  Phone,
  ThumbsDown,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { MakeCallModal } from '@/components/common/calls/make-call-modal/MakeCallModal';
import { SendFrontEmailDialog } from '@/components/matter/SendFrontEmailDialog';
import { SmsComposerModal } from '@/components/sms-composer/SmsComposerModal';
import {
  TASK_INSTRUCTIONS_EVENTS,
  TASK_INSTRUCTIONS_PROPERTIES,
} from '@/constants/analytics/task-instructions-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { useGetMatterFlattenedContextQuery } from '@/services/api/matterFlattenedContextService';
import type {
  ActionItem,
  ActionMeta,
  CallActionMeta,
  EmailActionMeta,
  SlackActionMeta,
  TextActionMeta,
} from '@/services/api/tasksService';
import {
  useGetTaskInstructionQuery,
  useSubmitTaskInstructionFeedbackMutation,
} from '@/services/api/tasksService';

import { useCallScriptPiP } from './useCallScriptPiP';

interface TaskInstructionCardProps {
  matterId: string;
  taskId: string;
}

export const TaskInstructionCard = ({
  matterId,
  taskId,
}: TaskInstructionCardProps) => {
  const [showReasoning, setShowReasoning] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [showFeedbackPopover, setShowFeedbackPopover] = useState(false);
  const [feedbackReason, setFeedbackReason] = useState('');

  // Action modal states
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [callMeta, setCallMeta] = useState<CallActionMeta | null>(null);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsMeta, setSmsMeta] = useState<TextActionMeta | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailMeta, setEmailMeta] = useState<EmailActionMeta | null>(null);
  const [slackPopoverOpen, setSlackPopoverOpen] = useState(false);
  const [slackMeta, setSlackMeta] = useState<SlackActionMeta | null>(null);


  const popoverRef = useRef<HTMLDivElement>(null);
  const { openScript, fallbackScript, closeFallback } = useCallScriptPiP();
  const capture = usePostHogCapture();

  const {
    data: instruction,
    isLoading,
    error,
  } = useGetTaskInstructionQuery(
    { matterId, taskId },
    { skip: !matterId || !taskId }
  );

  const { data: matterContext } = useGetMatterFlattenedContextQuery(
    { matterId },
    { skip: !matterId },
  );

  const [submitFeedback, { isLoading: isSubmitting }] =
    useSubmitTaskInstructionFeedbackMutation();

  // Fire once when instruction data arrives
  useEffect(() => {
    if (!instruction) return;
    capture(TASK_INSTRUCTIONS_EVENTS.CARD_VIEWED, {
      [TASK_INSTRUCTIONS_PROPERTIES.MATTER_ID]: matterId,
      [TASK_INSTRUCTIONS_PROPERTIES.TASK_ID]: taskId,
      [TASK_INSTRUCTIONS_PROPERTIES.INSTRUCTION_ID]: instruction.id,
      [TASK_INSTRUCTIONS_PROPERTIES.GENERATION_MODE]: instruction.generation_mode,
      [TASK_INSTRUCTIONS_PROPERTIES.CONFIDENCE]: instruction.confidence,
      [TASK_INSTRUCTIONS_PROPERTIES.ACTION_COUNT]: instruction.next_actions.length,
    });
    // Only fire on first load — instruction.id as dep ensures it re-fires on new instructions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instruction?.id]);

  if (isLoading) {
    return (
      <div className="rounded-md border border-slate-300 bg-slate-50 px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <span>Loading instructions...</span>
        </div>
      </div>
    );
  }

  if (error || !instruction) {
    return null;
  }

  const handleThumbsDown = () => {
    if (instruction.thumbs_down) return;
    setShowFeedbackPopover(true);
  };

  const handleSubmitFeedback = async () => {
    await submitFeedback({
      matterId,
      taskId,
      reason: feedbackReason || undefined,
    });
    capture(TASK_INSTRUCTIONS_EVENTS.FEEDBACK_SUBMITTED, {
      [TASK_INSTRUCTIONS_PROPERTIES.MATTER_ID]: matterId,
      [TASK_INSTRUCTIONS_PROPERTIES.TASK_ID]: taskId,
      [TASK_INSTRUCTIONS_PROPERTIES.INSTRUCTION_ID]: instruction.id,
      [TASK_INSTRUCTIONS_PROPERTIES.HAS_REASON]: Boolean(feedbackReason),
    });
    setShowFeedbackPopover(false);
    setFeedbackReason('');
  };

  const handleSkipFeedback = async () => {
    await submitFeedback({ matterId, taskId });
    capture(TASK_INSTRUCTIONS_EVENTS.FEEDBACK_SKIPPED, {
      [TASK_INSTRUCTIONS_PROPERTIES.MATTER_ID]: matterId,
      [TASK_INSTRUCTIONS_PROPERTIES.TASK_ID]: taskId,
      [TASK_INSTRUCTIONS_PROPERTIES.INSTRUCTION_ID]: instruction.id,
    });
    setShowFeedbackPopover(false);
    setFeedbackReason('');
  };

  const handleCallAction = (meta: CallActionMeta, index: number) => {
    capture(TASK_INSTRUCTIONS_EVENTS.ACTION_CLICKED, {
      [TASK_INSTRUCTIONS_PROPERTIES.MATTER_ID]: matterId,
      [TASK_INSTRUCTIONS_PROPERTIES.TASK_ID]: taskId,
      [TASK_INSTRUCTIONS_PROPERTIES.INSTRUCTION_ID]: instruction.id,
      [TASK_INSTRUCTIONS_PROPERTIES.ACTION_TYPE]: 'call',
      [TASK_INSTRUCTIONS_PROPERTIES.ACTION_INDEX]: index,
    });
    setCallMeta(meta);
    setCallModalOpen(true);
    if (meta.script) {
      openScript(meta.script, meta.contact_name ?? undefined);
    }
  };

  const handleTextAction = (meta: TextActionMeta, index: number) => {
    capture(TASK_INSTRUCTIONS_EVENTS.ACTION_CLICKED, {
      [TASK_INSTRUCTIONS_PROPERTIES.MATTER_ID]: matterId,
      [TASK_INSTRUCTIONS_PROPERTIES.TASK_ID]: taskId,
      [TASK_INSTRUCTIONS_PROPERTIES.INSTRUCTION_ID]: instruction.id,
      [TASK_INSTRUCTIONS_PROPERTIES.ACTION_TYPE]: 'text',
      [TASK_INSTRUCTIONS_PROPERTIES.ACTION_INDEX]: index,
    });
    setSmsMeta(meta);
    setSmsModalOpen(true);
  };

  const handleSlackAction = (meta: SlackActionMeta, index: number) => {
    capture(TASK_INSTRUCTIONS_EVENTS.ACTION_CLICKED, {
      [TASK_INSTRUCTIONS_PROPERTIES.MATTER_ID]: matterId,
      [TASK_INSTRUCTIONS_PROPERTIES.TASK_ID]: taskId,
      [TASK_INSTRUCTIONS_PROPERTIES.INSTRUCTION_ID]: instruction.id,
      [TASK_INSTRUCTIONS_PROPERTIES.ACTION_TYPE]: 'slack',
      [TASK_INSTRUCTIONS_PROPERTIES.ACTION_INDEX]: index,
    });
    setSlackMeta(meta);
    setSlackPopoverOpen(true);
  };

  const handleEmailAction = (meta: EmailActionMeta, index: number) => {
    capture(TASK_INSTRUCTIONS_EVENTS.ACTION_CLICKED, {
      [TASK_INSTRUCTIONS_PROPERTIES.MATTER_ID]: matterId,
      [TASK_INSTRUCTIONS_PROPERTIES.TASK_ID]: taskId,
      [TASK_INSTRUCTIONS_PROPERTIES.INSTRUCTION_ID]: instruction.id,
      [TASK_INSTRUCTIONS_PROPERTIES.ACTION_TYPE]: 'email',
      [TASK_INSTRUCTIONS_PROPERTIES.ACTION_INDEX]: index,
    });
    setEmailMeta(meta);
    setEmailModalOpen(true);
  };

  // Normalize items: handle both old string format and new ActionItem format
  const normalizeItem = (item: ActionItem | string): ActionItem => {
    if (typeof item === 'string') return { text: item, action: null };
    return item;
  };

  const clientName = matterContext?.clientName ?? 'Client';
  const clientPhone = matterContext?.clientPhone ?? undefined;
  const clientEmail = matterContext?.clientEmail ?? undefined;

  return (
    <>
      <div className="rounded-md border-2 border-slate-800 bg-white mb-2">
        {/* Header */}
        <button
          type="button"
          onClick={() => {
            const next = !collapsed;
            setCollapsed(next);
            capture(
              next
                ? TASK_INSTRUCTIONS_EVENTS.CARD_COLLAPSED
                : TASK_INSTRUCTIONS_EVENTS.CARD_EXPANDED,
              {
                [TASK_INSTRUCTIONS_PROPERTIES.MATTER_ID]: matterId,
                [TASK_INSTRUCTIONS_PROPERTIES.TASK_ID]: taskId,
                [TASK_INSTRUCTIONS_PROPERTIES.INSTRUCTION_ID]: instruction.id,
              },
            );
          }}
          className="flex w-full items-center justify-between px-3 pt-1.5 pb-1.5"
        >
          <p className="text-[11px] font-black uppercase tracking-tight text-slate-900">
            Instructions
          </p>
          <div className="flex items-center gap-1.5">
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleThumbsDown();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  handleThumbsDown();
                }
              }}
              className={`relative p-0.5 rounded transition-colors ${
                instruction.thumbs_down
                  ? 'text-red-500'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
              title={
                instruction.thumbs_down
                  ? 'Feedback submitted'
                  : 'Mark as unhelpful'
              }
            >
              <ThumbsDown
                className="h-3 w-3"
                fill={instruction.thumbs_down ? 'currentColor' : 'none'}
              />
            </span>
            {collapsed ? (
              <ChevronDown className="h-3.5 w-3.5 text-slate-800" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5 text-slate-800" />
            )}
          </div>
        </button>

        {/* Feedback popover */}
        {showFeedbackPopover && (
          <div
            ref={popoverRef}
            className="mx-3 mb-2 rounded-md border border-slate-200 bg-slate-50 p-2.5"
          >
            <p className="text-[11px] font-semibold text-slate-700 mb-1.5">
              What was wrong? (optional)
            </p>
            <textarea
              value={feedbackReason}
              onChange={(e) => setFeedbackReason(e.target.value)}
              placeholder="e.g. Wrong contact info, irrelevant suggestion..."
              className="w-full rounded border border-slate-300 px-2 py-1.5 text-[11px] text-slate-800 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none resize-none"
              rows={2}
            />
            <div className="flex justify-end gap-1.5 mt-1.5">
              <button
                type="button"
                onClick={handleSkipFeedback}
                disabled={isSubmitting}
                className="rounded px-2 py-1 text-[10px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={handleSubmitFeedback}
                disabled={isSubmitting}
                className="rounded bg-slate-800 px-2.5 py-1 text-[10px] font-medium text-white hover:bg-slate-700 transition-colors"
              >
                {isSubmitting ? 'Sending...' : 'Submit'}
              </button>
            </div>
          </div>
        )}

        {/* Collapsible content */}
        {!collapsed && (
          <>
            {instruction.recap && (
              <div className="px-3 pb-1.5">
                <p className="text-[12px] font-bold leading-snug text-slate-700">
                  {instruction.recap}
                </p>
              </div>
            )}

            {/* Next Actions */}
            <div className="px-3 pb-2 mt-1">
              <ol className="space-y-2">
                {instruction.next_actions.map((rawItem, i) => {
                  const item = normalizeItem(rawItem);
                  return (
                    <li key={i} className="flex gap-1.5 text-[11px]">
                      <span className="mt-px flex h-[14px] w-[14px] flex-shrink-0 items-center justify-center rounded-full bg-slate-900 text-[9px] font-bold text-white">
                        {i + 1}
                      </span>
                      <div className="flex-1">
                        <span className="font-bold text-xs text-slate-900">
                          {item.text}
                        </span>
                        {item.action && (
                          <ActionChip
                            action={item.action}
                            index={i}
                            onCall={handleCallAction}
                            onText={handleTextAction}
                            onEmail={handleEmailAction}
                            onSlack={handleSlackAction}
                          />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* PiP fallback: inline script panel */}
            {fallbackScript && (
              <div className="mx-3 mb-2 rounded border border-slate-300 bg-slate-50 p-2.5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-bold uppercase text-slate-500">
                    Call Script
                  </span>
                  <button
                    type="button"
                    onClick={closeFallback}
                    className="p-0.5 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <p className="text-[11px] leading-relaxed text-slate-800">
                  {fallbackScript}
                </p>
              </div>
            )}

            {/* Reasoning */}
            <div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const next = !showReasoning;
                  setShowReasoning(next);
                  capture(
                    next
                      ? TASK_INSTRUCTIONS_EVENTS.REASONING_EXPANDED
                      : TASK_INSTRUCTIONS_EVENTS.REASONING_COLLAPSED,
                    {
                      [TASK_INSTRUCTIONS_PROPERTIES.MATTER_ID]: matterId,
                      [TASK_INSTRUCTIONS_PROPERTIES.TASK_ID]: taskId,
                      [TASK_INSTRUCTIONS_PROPERTIES.INSTRUCTION_ID]: instruction.id,
                    },
                  );
                }}
                className="flex w-full items-center justify-between px-3 py-1.5 text-[11px] text-slate-600 hover:text-slate-800 transition-colors"
              >
                <span>Why?</span>
                {showReasoning ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </button>
              {showReasoning && (
                <div className="px-3 pb-2.5">
                  <p className="text-[11px] leading-relaxed text-slate-800">
                    {instruction.reasoning}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Call Modal */}
      {callMeta && (
        <MakeCallModal
          isOpen={callModalOpen}
          onClose={() => {
            setCallModalOpen(false);
            setCallMeta(null);
          }}
          initialPhoneNumber={callMeta.phone_number ?? undefined}
          initialContactLabel={callMeta.contact_name ?? undefined}
          matterId={matterId}
          taskId={taskId}
        />
      )}

      {/* Email Modal */}
      {emailMeta && (
        <SendFrontEmailDialog
          open={emailModalOpen}
          onClose={() => {
            setEmailModalOpen(false);
            setEmailMeta(null);
          }}
          matterId={matterId}
          taskId={taskId}
          defaultTo={
            emailMeta.to_email
              ? [emailMeta.to_email]
              : clientEmail
                ? [clientEmail]
                : undefined
          }
          defaultSubject={emailMeta.subject ?? undefined}
          defaultBody={emailMeta.body_draft ?? undefined}
        />
      )}

      {/* SMS Modal */}
      {smsMeta && (
        <SmsComposerModal
          open={smsModalOpen}
          onOpenChange={(open) => {
            setSmsModalOpen(open);
            if (!open) setSmsMeta(null);
          }}
          matterId={matterId}
          clientName={clientName}
          clientPhone={smsMeta.phone_number ?? clientPhone}
          taskId={taskId}
          initialMessage={smsMeta.message_draft ?? undefined}
        />
      )}

      {/* Slack popover */}
      {slackPopoverOpen && slackMeta && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-[400px] max-w-[90vw] rounded-lg border border-slate-200 bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-[#4A154B]" />
                <span className="text-sm font-semibold text-slate-900">
                  Slack {slackMeta.recipient_name ? `→ ${slackMeta.recipient_name}` : 'Message'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSlackPopoverOpen(false);
                  setSlackMeta(null);
                }}
                className="rounded p-0.5 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Message draft */}
            <div className="px-4 py-3">
              <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Draft Message
              </p>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">
                  {slackMeta.message_draft ?? '(No draft available)'}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end border-t border-slate-100 px-4 py-3">
              <a
                href={
                  slackMeta.slack_user_id
                    ? `https://finchlegal.slack.com/app_redirect?channel=${slackMeta.slack_user_id}`
                    : 'https://finchlegal.slack.com'
                }
                target="_blank"
                rel="noreferrer"
                onClick={async () => {
                  if (slackMeta.message_draft) {
                    try {
                      await navigator.clipboard.writeText(slackMeta.message_draft);
                    } catch {
                      // Silently fail if clipboard access is denied
                    }
                  }
                  setSlackPopoverOpen(false);
                  setSlackMeta(null);
                }}
                className="flex items-center gap-1.5 rounded bg-[#4A154B] px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-[#611f69] transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                Copy message &amp; open Slack
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// ActionChip — small inline button next to action text
// ---------------------------------------------------------------------------

const ActionChip = ({
  action,
  index,
  onCall,
  onText,
  onEmail,
  onSlack,
}: {
  action: ActionMeta;
  index: number;
  onCall: (meta: CallActionMeta, index: number) => void;
  onText: (meta: TextActionMeta, index: number) => void;
  onEmail: (meta: EmailActionMeta, index: number) => void;
  onSlack: (meta: SlackActionMeta, index: number) => void;
}) => {
  const chipBase =
    'ml-1.5 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide transition-colors cursor-pointer';

  switch (action.action_type) {
    case 'call':
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCall(action, index);
          }}
          className={`${chipBase} bg-green-100 text-green-800 hover:bg-green-200`}
        >
          <Phone className="h-2.5 w-2.5" />
          Call
        </button>
      );
    case 'text':
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onText(action, index);
          }}
          className={`${chipBase} bg-blue-100 text-blue-800 hover:bg-blue-200`}
        >
          <MessageSquare className="h-2.5 w-2.5" />
          Text
        </button>
      );
    case 'email':
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEmail(action, index);
          }}
          className={`${chipBase} bg-purple-100 text-purple-800 hover:bg-purple-200`}
        >
          <Mail className="h-2.5 w-2.5" />
          Email
        </button>
      );
    case 'slack':
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSlack(action, index);
          }}
          className={`${chipBase} bg-[#f5e6f5] text-[#4A154B] hover:bg-[#ead4ea]`}
        >
          <Hash className="h-2.5 w-2.5" />
          Slack
        </button>
      );
    default:
      return null;
  }
}
